/**
 * Password reset and change, with Firebase calls passed in so they can be tested.
 * describeError maps a Firebase auth error to customer text.
 */

const GENERIC = 'Something went wrong. Please try again.'

const authCode = (error) => String(error?.code || '').replace(/^auth\//, '')

export function hasEmailPasswordProvider(firebaseUser) {
  return Boolean(firebaseUser?.providerData?.some((p) => p?.providerId === 'password'))
}

// Same result whether or not the email has an account, so the form can't be used to probe emails.
export async function requestPasswordReset({ email, send, describeError }) {
  const address = String(email || '').trim()
  try {
    await send(address)
  } catch (error) {
    if (authCode(error) !== 'user-not-found') {
      throw new Error(describeError(error) || GENERIC)
    }
  }
  return address
}

/** Email accounts only: re-checks the current password, then sets the new one. */
export async function changeEmailPassword({
  firebaseUser,
  currentPassword,
  newPassword,
  credentialFor,
  reauthenticate,
  update,
  describeError
}) {
  if (!firebaseUser?.email || !hasEmailPasswordProvider(firebaseUser)) {
    throw new Error('Password changes are only for accounts that sign in with email.')
  }
  try {
    await reauthenticate(firebaseUser, credentialFor(firebaseUser.email, currentPassword))
  } catch (error) {
    const code = authCode(error)
    if (code === 'invalid-credential' || code === 'wrong-password') {
      throw new Error('Your current password is incorrect.')
    }
    throw new Error(describeError(error) || GENERIC)
  }
  try {
    await update(firebaseUser, newPassword)
  } catch (error) {
    throw new Error(describeError(error) || GENERIC)
  }
}
